import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { MultiSelect } from '@repo/ui/multiSelect'
import { ShieldAlert, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'

import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { SudoContextOrganization } from '@/store/sudo'
import { customValidationPasswords, userPasswordSchema, userSchema } from '@/validation-schemas'

export function UserCreator({ organizations }: { organizations: SudoContextOrganization[] }) {
  const [open, setOpen] = useState<boolean>(false)

  return (
    <>
      <Button variant="outline" className="w-fit" onClick={() => setOpen(true)}>
        <UserPlus className="size-4" />
        Create New User
      </Button>
      {open && <UserForm modalHandler={{ open, setOpen }} organizations={organizations} />}
    </>
  )
}

function UserForm({
  modalHandler,
  organizations,
}: {
  modalHandler: { open: boolean; setOpen: (open: boolean) => void }
  organizations: SudoContextOrganization[]
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT({ keyPrefix: 'manageUsers.modal' })

  // required to avoid looping
  const defaultValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      organizationIds: [],
      superAdmin: false,
      disabled: false,
    }),
    [],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      modalHandler.setOpen(false)
      void dispatch(
        sudoStoreActions.user.create({
          fields: values,
        }),
      )
      form.reset(defaultValues) // required
    },
    schema: { ...userSchema, ...userPasswordSchema },
    customValidation: (values) => {
      return customValidationPasswords(values.password, values.confirmPassword)
    },
  })

  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent className="max-h-screen overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>Add a new user. Click create when you're done.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} className="space-y-2" onKeyDown={submitOnPressMetaEnter}>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('firstName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={tc('firstName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('lastName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={tc('lastName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('email')}</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={tc('email')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('password')}</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder={tc('password')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('password')}</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder={tc('password')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="organizationIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc('organizations')}</FormLabel>
                  <FormControl>
                    <MultiSelect
                      className="border-primary-200 rounded-lg border p-4"
                      items={organizations.map((o) => ({
                        id: o.id,
                        label: o.name,
                      }))}
                      onSelection={field.onChange}
                      canUnselectAll={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="superAdmin"
              render={({ field }) => (
                <FormItem>
                  <div className="border-primary-200 flex items-center gap-2 rounded-md border p-3">
                    <div className="flex-shrink-0">
                      <ShieldAlert className="text-primary-600 h-6 w-6" />
                    </div>
                    <FormControl>
                      <Checkbox
                        id="superAdmin"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel htmlFor="superAdmin" className="cursor-pointer">
                      <strong className="text-highlight">Caution:</strong> check this box to give
                      this user the <strong>CaseAI-Super-Admin</strong> power.
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={!isFormValid}>
                {tc('create', { cfl: true })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
